import { firestore } from '../firebaseConfig'; // Firestore setup
import { collection, query, where, orderBy, limit, startAfter, getDocs, getDoc, or, and } from 'firebase/firestore';

/**
 * Fetches user sentences from Firestore with optional filters and sorting.
 * @param {string} userId - The user ID.
 * @param {object} options - Filter and sort options.
 * @param {string} [options.level] - Level filter ('all' to ignore).
 * @param {number} [options.srsStage] - SRS stage filter ('all' to ignore).
 * @param {string} [options.status] - Status filter ('all' or 'skipped').
 * @param {string} [options.sortBy] - Sort option ('level', 'srsStage', 'unlocked', 'nextReview').
 * @param {number} [options.limitValue] - The number of sentences to fetch.
 * @param {QueryDocumentSnapshot} [options.lastVisible] - The last document snapshot for pagination.
 * @returns {Promise<{sentences: Array, lastVisible: QueryDocumentSnapshot, hasMore: boolean}>} An object containing the sentences, the last visible doc, and a boolean indicating if there are more.
 */
export const fetchUserSentences = async (userId, { level, srsStage, status, sortBy, limitValue, lastVisible }) => {
  try {
    const userSentencesRef = collection(firestore, 'users', userId, 'user_sentences');
    let queryConstraints = [];
    let filterConstraints = []; // <-- Array for 'and' conditions

    // Handle the level filter
    if (level && level !== 'all') {
      filterConstraints.push(where('level', '==', parseInt(level)));
    }

    // Handle SRS stage and skipped status
    if (srsStage && srsStage === '7') {
      // Special case for the final SRS stage, which can include skipped items
      filterConstraints.push(or(where('srs_stage', '==', 7), where('skipped', '==', true)));
    } else {
      // Handle other SRS stages
      if (srsStage && srsStage !== 'all') {
        filterConstraints.push(where('srs_stage', '==', parseInt(srsStage)));
        filterConstraints.push(where('skipped', '==', false));
      }

      // Handle skipped status separately
      if (status === 'skipped') {
        filterConstraints.push(where('skipped', '==', true));
      }
    }

    // If there are any filter conditions, wrap them in a single 'and' clause
    if (filterConstraints.length > 0) {
      queryConstraints.push(and(...filterConstraints));
    }

    // Add sorting conditions - These remain at the top level
    switch (sortBy) {
      case 'level':
        queryConstraints.push(orderBy('level', 'desc'));
        break;
      case 'srsStage':
        queryConstraints.push(orderBy('srs_stage'));
        break;
      case 'unlocked':
        queryConstraints.push(orderBy('learned_at', 'desc'));
        break;
      case 'nextReview':
        queryConstraints.push(orderBy('next_review', 'asc'));
        break;
      default:
        // A default sort is crucial for consistent pagination
        queryConstraints.push(orderBy('learned_at', 'desc'));
        break;
    }

    // Add pagination constraints
    if (lastVisible) {
      queryConstraints.push(startAfter(lastVisible));
    }

    queryConstraints.push(limit(limitValue));

    const sentenceQuery = query(userSentencesRef, ...queryConstraints);
    const querySnapshot = await getDocs(sentenceQuery);

    const sentencesArray = await Promise.all(
      querySnapshot.docs.map(async (docSnapshot) => {
        const sentenceData = docSnapshot.data();
        const sentenceRef = sentenceData.sentence_ref;
        const sentenceDoc = await getDoc(sentenceRef);
        const sentenceDetails = sentenceDoc.exists() ? sentenceDoc.data() : {};

        return {
          id: docSnapshot.id,
          srsStage: sentenceData.srs_stage,
          ...sentenceData,
          ...sentenceDetails,
        };
      })
    );

    const newLastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
    const hasMore = querySnapshot.docs.length === limitValue;

    return { sentences: sentencesArray, lastVisible: newLastVisible, hasMore };
  } catch (error) {
    console.error('Error fetching user sentences:', error);
    // Return a consistent shape on error to prevent crashes in the component
    return { sentences: [], lastVisible: null, hasMore: false };
  }
};

/**
 * Fetches user words from Firestore with optional filters and sorting.
 * @param {string} userId - The user ID.
 * @param {object} options - Filter and sort options.
 * @param {string} [options.level] - Level filter ('all' to ignore).
 * @param {number} [options.srsStage] - SRS stage filter ('all' to ignore).
 * @param {string} [options.status] - Status filter ('all' or 'skipped').
 * @param {string} [options.sortBy] - Sort option ('level', 'srsStage', 'unlocked', 'nextReview').
 * @param {number} [options.limitValue] - The number of words to fetch.
 * @param {QueryDocumentSnapshot} [options.lastVisible] - The last document snapshot for pagination.
 * @returns {Promise<{words: Array, lastVisible: QueryDocumentSnapshot, hasMore: boolean}>} An object containing the words, the last visible doc, and a boolean indicating if there are more.
 */
export const fetchUserWords = async (userId, { level, srsStage, status, sortBy, limitValue, lastVisible }) => {
  try {
    const userWordsRef = collection(firestore, 'users', userId, 'user_words');
    let queryConstraints = [];
    let filterConstraints = []; // <-- Array for 'and' conditions

    // Handle the level filter
    if (level && level !== 'all') {
      filterConstraints.push(where('level', '==', parseInt(level)));
    }

    // Handle SRS stage and skipped status
    if (srsStage && srsStage === '7') {
      // This is a special case for the 'Titanium' stage which can include skipped
      filterConstraints.push(or(where('srs_stage', '==', 7), where('skipped', '==', true)));
    } else {
      // Handle other SRS stages
      if (srsStage && srsStage !== 'all') {
        filterConstraints.push(where('srs_stage', '==', parseInt(srsStage)));
        filterConstraints.push(where('skipped', '==', false));
      }
      // Handle skipped status separately if a specific SRS stage isn't selected
      if (status === 'skipped') {
        filterConstraints.push(where('skipped', '==', true));
      }
    }

    // If there are any filter conditions, wrap them in an 'and'
    if (filterConstraints.length > 0) {
      queryConstraints.push(and(...filterConstraints));
    }

    // Add sorting conditions - These remain at the top level
    switch (sortBy) {
      case 'level':
        queryConstraints.push(orderBy('level', 'desc'));
        break;
      case 'srsStage':
        queryConstraints.push(orderBy('srs_stage'));
        break;
      case 'unlocked':
        queryConstraints.push(orderBy('learned_at', 'desc'));
        break;
      case 'nextReview':
        queryConstraints.push(orderBy('next_review', 'asc'));
        break;
      default:
        // A default sort is recommended for consistent pagination
        queryConstraints.push(orderBy('learned_at', 'desc'));
        break;
    }

    // Add pagination constraints
    if (lastVisible) {
      queryConstraints.push(startAfter(lastVisible));
    }

    queryConstraints.push(limit(limitValue));

    const wordQuery = query(userWordsRef, ...queryConstraints);
    const querySnapshot = await getDocs(wordQuery);

    const wordsArray = await Promise.all(
      querySnapshot.docs.map(async (docSnapshot) => {
        const wordData = docSnapshot.data();
        const wordRef = wordData.word_ref;
        const wordDoc = await getDoc(wordRef);
        const wordDetails = wordDoc.exists() ? wordDoc.data() : {};

        return {
          id: docSnapshot.id,
          srsStage: wordData.srs_stage,
          ...wordData,
          ...wordDetails,
        };
      })
    );

    const newLastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
    const hasMore = querySnapshot.docs.length === limitValue;

    return { words: wordsArray, lastVisible: newLastVisible, hasMore };
  } catch (error) {
    console.error('Error fetching user words:', error);
    // It's often better to return a consistent shape on error
    return { words: [], lastVisible: null, hasMore: false };
  }
};
